// F-092 Propriétaires mandants : liste, accès au portail, documents partagés, synthèse.
import { useState } from "react";
import { Link } from "react-router";
import { del, post } from "@core/api";
import { dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, Empty, ErrorBox, Input, KeyValues, Loading, Modal, Notice, PageHeader, Select, Table, fieldError } from "@core/ui";
import { OWNER_KINDS, Status, accessState, docUrl, useQuick, useRun } from "./shared";

export function OwnersPage() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/owners"));
  const [invite, setInvite] = useState<any>(null);
  const [share, setShare] = useState<any>(null);
  const [detail, setDetail] = useState<string | null>(null);
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Propriétaires mandants"
        intro="Chaque propriétaire ou SCI peut consulter, en lecture, ses logements, ses baux, les loyers encaissés, les dépenses, les documents que vous partagez et ses rapports. Il ne voit jamais les autres mandants, les dossiers des candidats ni vos notes internes."
      />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && !q.data.items.length ? (
        <Card>
          <Empty title="Aucun mandant">Ajoutez un mandant ci-dessous ou importez vos mandants depuis un fichier CSV.</Empty>
        </Card>
      ) : null}
      {q.data?.items.length ? (
        <Card title="Mandants">
          <Table caption="Mandants">
            <thead>
              <tr>
                <th>Mandant</th>
                <th>Type</th>
                <th className="num">Logements</th>
                <th>Seuil d’accord</th>
                <th>Portail</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((o: any) => {
                const active = (o.access as any[]).filter((a) => ["active", "pending"].includes(accessState(a)));
                return (
                  <tr key={o.id}>
                    <td>
                      {o.display_name}
                      {o.email ? <span className="block muted small">{o.email}</span> : null}
                    </td>
                    <td>{OWNER_KINDS[o.kind] ?? o.kind}</td>
                    <td className="num">{o.properties}</td>
                    <td>{o.threshold_cents !== null ? `au-delà de ${money(o.threshold_cents)}` : "Règle par défaut"}</td>
                    <td>{active.length ? active.map((a) => <Status key={a.id} kind="access" value={accessState(a)} />) : <span className="muted">Aucun accès</span>}</td>
                    <td className="right">
                      <div className="row">
                        <Button variant="discret" small onClick={() => setDetail(o.id)}>
                          Synthèse
                        </Button>
                        {org.can("members") ? (
                          <Button variant="secondaire" small onClick={() => setInvite(o)}>
                            Inviter au portail
                          </Button>
                        ) : null}
                        {org.can("operate") ? (
                          <Button variant="discret" small onClick={() => setShare(o)}>
                            Partager un document
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      ) : null}
      {org.can("operate") ? <CreateOwner /> : null}
      {invite ? <InviteModal owner={invite} onClose={() => setInvite(null)} /> : null}
      {share ? <ShareModal owner={share} onClose={() => setShare(null)} /> : null}
      {detail ? <OwnerDetail ownerId={detail} onClose={() => setDetail(null)} /> : null}
    </div>
  );
}

function CreateOwner() {
  const org = useOrg();
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ kind: "sci", display_name: "", legal_name: "", siren: "", email: "", phone: "" });
  return (
    <Card title="Ajouter un mandant">
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          if (await run(() => post(org.api("/pro/owners"), form), "Mandant ajouté.")) {
            setForm({ ...form, display_name: "", legal_name: "", siren: "", email: "", phone: "" });
            invalidate(org.api("/pro"));
          }
        }}
      >
        <div className="form-row">
          <Select label="Type" value={form.kind} options={Object.entries(OWNER_KINDS).map(([value, label]) => ({ value, label }))} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
          <Input label="Nom affiché" required value={form.display_name} error={fieldError(error, "display_name")} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          <Input label="Dénomination légale" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
        </div>
        <div className="form-row">
          <Input label="SIREN" inputMode="numeric" value={form.siren} error={fieldError(error, "siren")} onChange={(e) => setForm({ ...form, siren: e.target.value })} />
          <Input label="Adresse e-mail" type="email" value={form.email} error={fieldError(error, "email")} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Téléphone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <ErrorBox error={error && !fieldError(error, "display_name") && !fieldError(error, "siren") && !fieldError(error, "email") ? error : null} />
        <div>
          <Button type="submit" variant="secondaire" busy={busy}>
            Ajouter
          </Button>
        </div>
      </form>
    </Card>
  );
}

function InviteModal({ owner, onClose }: { owner: any; onClose: () => void }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [email, setEmail] = useState(owner.email ?? "");
  const [canApprove, setCanApprove] = useState(true);
  return (
    <Modal
      open
      onClose={onClose}
      title={`Inviter au portail — ${owner.display_name}`}
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Annuler
          </Button>
          <Button
            busy={busy}
            onClick={async () => {
              if (await run(() => post(org.api(`/pro/owners/${owner.id}/access`), { email, can_approve: canApprove }), "Invitation envoyée.")) {
                invalidate(org.api("/pro"));
                onClose();
              }
            }}
          >
            Envoyer l’invitation
          </Button>
        </>
      }
    >
      <p>La personne invitée crée un compte (ou se connecte) avec cette adresse, puis accède au portail en lecture. Le lien est valable 14 jours.</p>
      <Input label="Adresse e-mail du représentant" type="email" required value={email} error={fieldError(error, "email")} onChange={(e) => setEmail(e.target.value)} />
      <Checkbox label="Autoriser la réponse aux demandes d’accord de dépense" checked={canApprove} onChange={setCanApprove} help="Sinon, l’accès reste en lecture seule." />
      <ErrorBox error={error && !fieldError(error, "email") ? error : null} />
    </Modal>
  );
}

function ShareModal({ owner, onClose }: { owner: any; onClose: () => void }) {
  const org = useOrg();
  const docs = useGet<any>(org.api("/documents"));
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [docId, setDocId] = useState("");
  return (
    <Modal
      open
      onClose={onClose}
      title={`Partager un document — ${owner.display_name}`}
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Annuler
          </Button>
          <Button
            busy={busy}
            disabled={!docId}
            onClick={async () => {
              if (await run(() => post(org.api(`/pro/owners/${owner.id}/documents`), { document_id: docId }), "Document partagé.")) {
                invalidate(org.api("/pro"));
                onClose();
              }
            }}
          >
            Partager
          </Button>
        </>
      }
    >
      <p className="small muted">Seules les pièces de l’espace hors candidature peuvent être partagées : les pièces d’un dossier de candidat ne le sont jamais.</p>
      {docs.isLoading ? <Loading /> : null}
      <Select
        label="Document"
        value={docId}
        options={[{ value: "", label: "Choisir un document" }, ...(docs.data?.items ?? []).map((d: any) => ({ value: d.id, label: d.title || d.filename }))]}
        onChange={(e) => setDocId(e.target.value)}
      />
      <ErrorBox error={error} />
    </Modal>
  );
}

function OwnerDetail({ ownerId, onClose }: { ownerId: string; onClose: () => void }) {
  const org = useOrg();
  const q = useGet<any>(org.api(`/pro/owners/${ownerId}`));
  const invalidate = useInvalidate();
  const quick = useQuick();
  const d = q.data;
  return (
    <Modal open onClose={onClose} title={d ? `Synthèse — ${d.owner.display_name}` : "Synthèse"}>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {d ? (
        <div className="stack">
          <Notice>Ce que voit le mandant sur son portail, plus la gestion de ses accès.</Notice>
          <KeyValues
            items={[
              ["Logements", d.properties.map((p: any) => p.label).join(", ") || "—"],
              ["Baux", String(d.leases.length)],
              ["Encaissements sur 12 mois", money(d.rents.paid_cents)],
              ["Loyers appelés sur 12 mois", money(d.rents.called_cents)],
              ["Dépenses suivies", String(d.expenses.length)],
              ["Rapports", String(d.reports.length)],
            ]}
          />
          <h3>Documents partagés</h3>
          {d.documents.length ? (
            <ul className="list-plain">
              {d.documents.map((doc: any) => (
                <li key={doc.id} className="row-between">
                  <a href={docUrl(doc.id)} target="_blank" rel="noreferrer">
                    {doc.title || doc.filename}
                  </a>
                  {org.can("operate") ? (
                    <Button variant="discret" small onClick={async () => (await quick(() => del(org.api(`/pro/owners/${ownerId}/documents/${doc.id}`)), "Partage retiré.")) && invalidate(org.api("/pro"))}>
                      Retirer le partage
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucun document partagé.</p>
          )}
          <h3>Accès au portail</h3>
          {d.access.length ? (
            <ul className="list-plain">
              {d.access.map((a: any) => (
                <li key={a.id} className="row-between">
                  <span>
                    {a.email} <Status kind="access" value={accessState(a)} />
                    <span className="block muted small">{a.accepted_at ? `Accepté le ${dateTime(a.accepted_at)}` : `Valable jusqu’au ${dateTime(a.expires_at)}`}</span>
                  </span>
                  {org.can("members") && !a.revoked_at ? (
                    <Button variant="danger" small onClick={async () => (await quick(() => post(org.api(`/pro/owner-access/${a.id}/revoke`)), "Accès retiré.")) && invalidate(org.api("/pro"))}>
                      Retirer l’accès
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucun accès. Invitez un représentant depuis la liste.</p>
          )}
          <p className="small">
            <Link to={org.to("professionnels/depenses")}>Régler le seuil d’accord des dépenses</Link> · <Link to={org.to("professionnels/rapports")}>Générer un rapport</Link>
          </p>
        </div>
      ) : null}
    </Modal>
  );
}
